import React from 'react';
import { Edit, FolderOpen, AlertTriangle, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { formatSimpleDate } from '../utils/dateFormatter';

const LogCard = ({ log, onEdit, onView }) => {
  const user = log.users || {};
  const isInactive = user.is_active === false;
  const userName = isInactive && user.inactive_user_name 
    ? user.inactive_user_name 
    : (user.name || 'Unknown User');
  const driveFolderLink = user.drive_folder_link;
  const hasBlockers = log.blockers && log.blockers.trim() !== '';

  const getLogDate = (log) => {
    // Check the correct database column 'log_date' first
    if (log.log_date) return new Date(log.log_date);
    // Fallback to 'date' (if legacy) or 'created_at'
    if (log.date) return new Date(log.date);
    return new Date(log.created_at);
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'done':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'in_progress':
      case 'pending':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'draft':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  const handleOpenDriveFolder = (e) => {
    e.stopPropagation();
    if (driveFolderLink) {
      window.open(driveFolderLink, '_blank');
    }
  };

  return (
    <div
      className={`glass-card p-6 relative ${
        hasBlockers ? 'border-2 border-red-500/50' : ''
      }`}
      data-testid={`log-card-${log.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold" data-testid="log-user-name">
            {userName}
          </h3>
          <span className="text-xs font-medium text-gray-400 bg-gray-800/50 px-2 py-1 rounded-md border border-gray-700/50">
            {format(getLogDate(log), 'MMM dd, yyyy')}
          </span>
        </div>

        {/* Folder Icon Button */}
        {driveFolderLink && (
          <button
            onClick={handleOpenDriveFolder}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-blue-400"
            title="Open Drive Folder"
            data-testid="log-drive-button"
          >
            <FolderOpen size={18} />
          </button>
        )}
      </div>

      {/* Blockers Warning */}
      {hasBlockers && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-red-500/10 rounded-lg border border-red-500/30">
          <AlertTriangle className="text-red-400" size={16} />
          <span className="text-sm text-red-400 font-medium">Has Blockers</span>
        </div>
      )}

      {/* Body */}
      <div className="space-y-4 mb-4">
        {/* Today's Focus */}
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-1">Today's Focus</h4>
          <p className="text-sm text-gray-400 whitespace-pre-wrap" data-testid="log-focus">
            {log.focus || '—'}
          </p>
        </div>

        {/* Work Done */}
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-1">Work Done</h4>
          <p className="text-sm text-gray-400 whitespace-pre-wrap" data-testid="log-work-done">
            {log.work_done || '—'}
          </p>
        </div>

        {/* Blockers */}
        {hasBlockers && (
          <div>
            <h4 className="text-sm font-medium text-red-400 mb-1">Blockers</h4>
            <p className="text-sm text-red-300 whitespace-pre-wrap" data-testid="log-blockers">
              {log.blockers}
            </p>
          </div>
        )}

        {/* Next Steps */}
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-1">Next Steps</h4>
          <p className="text-sm text-gray-400 whitespace-pre-wrap" data-testid="log-next-steps">
            {log.next_steps || '—'}
          </p>
        </div>
      </div>

      {/* Footer with Updated Date */}
      {log.updated_at && (
        <div className="flex justify-center pt-4 border-t border-white/10 mb-4">
          <span className="text-xs text-gray-500" data-testid="log-updated-date">
            Updated: {formatSimpleDate(log.updated_at)}
          </span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
        <button
          onClick={onView}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors"
          data-testid="log-view-button"
        >
          <Eye size={16} />
          View
        </button>
        <button
          onClick={onEdit}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors"
          data-testid="log-edit-button"
        >
          <Edit size={16} />
          Edit
        </button>
      </div>
    </div>
  );
};

export default LogCard;

